import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useGetProviderBreakdown } from "@/hooks/use-inboxplacement";
import {
  useMailboxesbyEmail,
  useUpdateMailboxWarmup,
} from "@/hooks/use-mailbox";
import { cn, extractErrorMessage } from "@/lib/utils";
import { MailboxType } from "@/types/mailbox.types";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertCircle, ArrowUpRight, Loader } from "lucide-react";
import { toast } from "sonner";
import { usePlacementContext } from "./inbox-placement-report/placement-context";

const PlacementStartWarmup = () => {
  const { state, summary } = usePlacementContext();
  const { isLoading: providerBreakdownLoading } = useGetProviderBreakdown(
    state.params.email,
    state.params.testId
  );

  const { data: mailbox } = useMailboxesbyEmail(state.params.email);
  const { mutateAsync: updateMailboxWarmup, isPending: isUpdatingWarmup } =
    useUpdateMailboxWarmup();

  const queryClient = useQueryClient();

  const handleStartWarmup = async () => {
    if (!mailbox?._id) return;

    try {
      await updateMailboxWarmup({
        id: mailbox._id,
        mailboxData: {
          isWarmupEnabled: !mailbox?.isWarmupEnabled,
        },
      });
      toast.success(
        mailbox?.isWarmupEnabled
          ? "Warmup disabled successfully"
          : "Warmup started successfully"
      );

      const eventName = mailbox?.isWarmupEnabled
        ? "inbox_placement_warmup_disabled"
        : "inbox_placement_warmup_started";

      window.cioanalytics?.track(eventName, {
        email: mailbox?.email,
        type: state.params.type,
      });

      queryClient.setQueryData(
        ["mailboxes", { email: mailbox?.email }],
        (oldData: MailboxType) => {
          return {
            ...oldData,
            isWarmupEnabled: !mailbox?.isWarmupEnabled,
          };
        }
      );
    } catch (error) {
      const message = extractErrorMessage(
        error,
        `Failed to ${mailbox?.isWarmupEnabled ? "disable" : "start"} warmup`
      );
      toast.error(message);
    }
  };

  if (summary?.spam?.count === 0) {
    return null;
  }

  return providerBreakdownLoading ? (
    <Skeleton className="h-24 w-full" />
  ) : (
    <Alert
      variant="destructive"
      className={cn(
        "bg-red-50 my-4 border-red-200 dark:bg-red-900/20 dark:border-red-900/20 flex flex-col md:flex-row md:items-center justify-between gap-4",
        state.isPrinting && "hidden"
      )}
    >
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
        <div>
          <AlertTitle className="text-red-600 dark:text-red-400">
            Deliverability Issues Detected
          </AlertTitle>
          <AlertDescription className="text-red-600 dark:text-red-400">
            Your emails are landing in spam folders for some providers. We
            recommend warming up your inbox.
          </AlertDescription>
        </div>
      </div>

      {mailbox ? (
        <div className="flex items-center gap-2 ml-5 md:ml-0">
          {isUpdatingWarmup ? (
            <Loader className="size-4 animate-spin" />
          ) : (
            <Switch
              checked={mailbox?.isWarmupEnabled || false}
              onCheckedChange={handleStartWarmup}
              disabled={isUpdatingWarmup}
              className=""
            />
          )}
          <span className="text-sm text-red-600 dark:text-red-400 whitespace-nowrap">
            {mailbox.isWarmupEnabled
              ? "Warmup Active"
              : isUpdatingWarmup || mailbox?.isWarmupEnabled
              ? "Starting Warmup..."
              : "Enable Warmup"}
          </span>
        </div>
      ) : (
        <Link to="/mailboxes">
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-600 border-red-200 hover:bg-red-100"
          >
            <span>Warm up your inbox</span>
            <ArrowUpRight className="h-4 w-4" />
          </Button>
        </Link>
      )}
    </Alert>
  );
};

export default PlacementStartWarmup;
